import * as React from 'react';
import { Program } from '~source/core/models/tv-guide';
import { Modal } from '~source/ui/components/atoms/modal/Modal';
import { Icon } from '~source/ui/components/atoms/icon/icon';
import { Button } from '~source/ui/components/atoms/button/button';
import { formatDay } from '~source/utils/format-day';
import { dateToTimeNotation } from '~source/utils/beautify-timing';
import { calculateDuration } from '~source/utils/calculate-duration';
import { isLiveProgram } from '~source/utils/is-live-program';
import { isMobileEpgPath } from '~source/utils/is-mobile-epg';
import { navigateToEpgDeepLink } from '~source/utils/epg-deeplink';
import { navigateToEpgWebUrl } from '~source/utils/epg-web-url';
import { getChannelLogoPath } from '~source/utils/get-channel-logo-path';
import $ from './program-modal.scss';

export interface ProgramModalProps {
    program: Program;
    channelName: string;
    onClose: () => void;
}

const isProgramReplay = (program: Program): boolean => {
    const now = new Date();
    const endDate = new Date(program.dateEnd);
    return endDate.getTime() < now.getTime();
};

const isProgramFuture = (program: Program): boolean => {
    const now = new Date();
    const startDate = new Date(program.dateStart);
    return startDate.getTime() > now.getTime();
};

export const ProgramModal: React.FC<ProgramModalProps> = ({ program, channelName, onClose }) => {
    const startDate = new Date(program.dateStart);
    const endDate = new Date(program.dateEnd);
    const duration = calculateDuration(startDate, endDate);
    const day = formatDay(program.dateStart);
    const startTime = dateToTimeNotation(startDate);
    const endTime = dateToTimeNotation(endDate);
    const isLive = isLiveProgram(program);
    const isReplay = isProgramReplay(program);
    const isFuture = isProgramFuture(program);
    const inApp = isMobileEpgPath();

    // Determine button text and visibility
    const showButton = isLive || isReplay || (isFuture && inApp);
    const buttonText = isLive || isReplay ? 'Afspelen' : 'Herinnering';

    const closemodal = () => {
        // Defer close to next frame to prevent click from "falling through" to elements beneath the modal
        // This is necessary because React portals can cause click events to dispatch to underlying elements
        // when the modal is synchronously unmounted during the click event
        requestAnimationFrame(() => {
            onClose();
        });
    };

    const handleCtaClick = () => {
        if (!inApp) {
            // Web: navigate to player page (autoplay is handled by the player page)
            navigateToEpgWebUrl(program);
        } else if (isLive || isReplay) {
            // Mobile: Play action
            navigateToEpgDeepLink(program);
        } else if (isFuture) {
            // Mobile: Reminder action (follow-my-sports)
            window.location.href = 'zst://follow-my-sports/';
        }
        closemodal();
    };

    return (
        <Modal programModal>
            <div className={$.content}>
                <button
                    className={$['close-button']}
                    onClick={(e) => {
                        // Stop the native event from propagating to prevent click-through
                        e.nativeEvent.stopImmediatePropagation();
                        closemodal();
                    }}
                    aria-label='Sluiten'
                >
                    <Icon type='close' size={24} />
                </button>
                <h2 className={$.title}>{program.title}</h2>

                <div className={$['meta-line']}>
                    <img
                        src={getChannelLogoPath(channelName)}
                        alt={channelName}
                        className={$['channel-logo']}
                    />
                    <span className={$['meta-text']}>
                        {day}, {startTime} - {endTime} • {duration}
                        {program.sportName && (
                            <>
                                {' • '}
                                <span className={$['sport-name']}>{program.sportName}</span>
                            </>
                        )}
                    </span>
                </div>

                {program.description && (
                    <div className={$.description}>
                        <p>{program.description}</p>
                    </div>
                )}

                {showButton && (
                    <div className={$['cta-container']}>
                        <Button
                            label={buttonText}
                            variant='solid'
                            onClick={handleCtaClick}
                            className={$['cta-button']}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};
